import { Box } from '@mui/material'
import { css, type Theme } from '@emotion/react'
import styled from '@emotion/styled'

import * as TitleStyles from '@components/shared/Title/styles'

type SummaryVariant = 'returned' | 'autoSend'

const summaryColors = (theme: Theme) => ({
  returned: { color: theme.colors.error.color, background: '#FDECEC', border: theme.colors.red.background },
  autoSend: { color: theme.colors.teal.color, background: '#C9F4F4', border: '#8FE3E3' }
})

export const Header = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`

// Mesma fonte dos títulos de seção do sistema (shared/Title), sem a margem inferior
export const HeaderTitle = styled(TitleStyles.Label)`
  margin-bottom: 0;
`

export const CompetenceBadge = styled(Box)`
  ${({ theme }) => css`
    padding: 1px 6px;
    border: 1px solid ${theme.colors.primary};
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    color: ${theme.colors.primary};
  `}
`

export const SummaryCards = styled(Box)`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    @media (max-width: ${theme.screenSizes.tablet}) {
      grid-template-columns: 1fr;
    }
  `}
`

export const SummaryCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant'
})<{ variant: SummaryVariant }>`
  ${({ theme, variant }) => css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px 16px;
    border-radius: 8px;
    border: 2px solid ${summaryColors(theme)[variant].border};
    background: ${summaryColors(theme)[variant].background};
    color: ${summaryColors(theme)[variant].color};
    box-shadow: 4px 4px 8px 0 ${theme.colors.background.hover};
  `}
`

export const SummaryCardHeader = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

export const SummaryCardTitle = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: inherit;
`

export const SummaryCardIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant'
})<{ variant: SummaryVariant }>`
  ${({ theme, variant }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    color: ${summaryColors(theme)[variant].color};
    background: ${summaryColors(theme)[variant].border};
  `}
`

export const SummaryCardValue = styled(Box)`
  ${({ theme }) => css`
    font-size: 20px;
    font-weight: 700;
    color: ${theme.colors.secondary};
  `}
`

export const SummaryCardDescription = styled(Box)`
  ${({ theme }) => css`
    font-size: 12px;
    color: ${theme.colors.secondary};
  `}
`

export const Divider = styled(Box)`
  ${({ theme }) => css`
    margin: 16px 0;
    border-top: 1px solid ${theme.colors.border.light};
  `}
`

export const TableToolbar = styled(Box)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`

export const UpdatedAt = styled(Box)`
  ${({ theme }) => css`
    font-size: 11px;
    color: ${theme.colors.gray[400]};
  `}
`

export const CellStack = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: nowrap;
`

export const CellTitle = styled(Box)`
  ${({ theme }) => css`
    font-size: 13px;
    font-weight: 600;
    color: ${theme.colors.secondary};
  `}
`

export const CellSubtitle = styled(Box)`
  ${({ theme }) => css`
    font-size: 11px;
    color: ${theme.colors.primary};
    opacity: 0.7;
  `}
`

export const AutoSendPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAutoSend'
})<{ isAutoSend: boolean }>`
  ${({ theme, isAutoSend }) => css`
    display: inline-block;
    min-width: 80px;
    padding: 2px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${isAutoSend ? theme.colors.teal.color : theme.colors.gray[400]};
    background: ${isAutoSend ? '#C9F4F4' : theme.colors.gray[200]};
  `}
`

export const UserInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const Avatar = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    flex-shrink: 0;
    font-size: 11px;
    color: ${theme.colors.lightest};
    background: ${theme.colors.gray[300]};
  `}
`
